import { useState } from 'react'
import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { FAQItem, H2, H3, Link, EtAiText } from '../components/ui'
import { SEO } from '../components/SEO/SEO'

export const Help = () => {
  const [openFAQ, setOpenFAQ] = useState(null)

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index)
  }

  const seoConfig = {
    title: 'Et Ai Help - How to Use Our AI Research Tool',
    description:
      'Frequently asked questions about the Et Ai closed beta. Learn how we are different from other AI tools and how to join our research community.',
    keywords:
      'Et Ai help, Et Ai beta, AI research tool FAQ, cognitive neuroscience AI, hallucination-free AI',
    url: 'https://etai.app/help',
    canonical: 'https://etai.app/help',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How is Et Ai different from ChatGPT or other AI research tools?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Et Ai uses a proprietary structured knowledge database, not a search-and-summarize method. We deconstruct the full text of scientific papers to build a map of science, which allows us to deliver comprehensive, hallucination-free insights from the entire scientific corpus, unlike general LLMs or other AI research assistants.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I join the closed beta?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "To join the beta, you must join our official Discord server. This gives you direct access to the team for feedback and support, and puts you at the center of our development community. Once you're in, you will find instructions to get immediate access to the platform.",
          },
        },
      ],
    },
  }

  const faqs = [
    {
      question: 'What problem does Et Ai solve?',
      answer: (
        <div>
          <p>
            The promise of AI in research is held back by a critical flaw: unreliability. Large
            Language Models frequently &quot;hallucinate&quot;; inventing facts, misquoting sources,
            and creating fake citations. For a scientist, academic, or researcher, even the mere{' '}
            <em>possibility</em> of this is unacceptable.
            <br />
            <EtAiText /> was built from the ground up to solve this problem. We provide a powerful
            research platform that is designed to be completely hallucination-free, ensuring every
            insight is accurate, verifiable, and traced directly back to the peer-reviewed
            literature.
          </p>
        </div>
      ),
    },
    {
      question: 'How is Et Ai different from ChatGPT or other AI research tools?',
      answer: (
        <div>
          <p className="mb-4">
            The difference lies in our fundamental approach to processing information. We see the
            landscape in three distinct categories:
          </p>
          <div className="space-y-3">
            <p>
              <strong>1. General AI Chatbots (e.g., ChatGPT, Gemini):</strong> Designed to generate
              plausible text, their tendency to &quot;hallucinate&quot; facts makes them
              untrustworthy for serious research.
            </p>
            <p>
              <strong>2. Existing AI Research Assistants and Deep Research Tools:</strong> These
              tools typically &quot;search-and-summarize&quot; a small number of papers, often just
              the abstracts. This can be superficial and miss the broader scientific consensus.
            </p>
            <p>
              <strong>
                3. <EtAiText />
                &apos;s Approach (A Structured Knowledge Database):
              </strong>{' '}
              We don&apos;t just search and summarize; we are building a permanent{' '}
              <strong>map of science</strong> itself. Our pipeline deconstructs the{' '}
              <strong>full text</strong> of every paper into its core components. When you ask a
              question, we query this comprehensive map for validated facts, delivering a deeper,
              more reliable understanding.
            </p>
          </div>
        </div>
      ),
    },
    {
      question: 'Who is Et Ai designed for?',
      answer: (
        <div>
          <p>
            <EtAiText /> is designed for anyone who requires rigorous, accurate, and verifiable
            insights from scientific literature. Our primary users include graduate students,
            postdoctoral researchers, university faculty, and scientists in corporate R&D
            departments. If you value your time and stake your work on the correctness of your
            sources, <EtAiText /> is built for you.
          </p>
        </div>
      ),
    },
    {
      question: 'How do I join the closed beta?',
      answer: (
        <div>
          <p>
            <a
              href="https://discord.gg/txuZbDUzdx"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 mb-3 rounded-lg transition-colors inline-block mt-4"
            >
              Click here to join our Discord
            </a>
            <br />
            This will take you to our official Discord server. Once you join, you&apos;ll find
            simple instructions on how to get immediate access to the beta.
          </p>
        </div>
      ),
    },
    {
      question: 'Why do I need to join Discord to participate in the beta?',
      answer: (
        <div>
          <p>
            We&apos;ve built our beta program around a community because we want to build{' '}
            <EtAiText /> <em>with</em> you, not just <em>for</em> you. Discord gives us a direct
            line to our beta testers, allowing for:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              <strong>Real-time Feedback:</strong> Instantly share your thoughts and ideas with the
              founders.
            </li>
            <li>
              <strong>Immediate Support:</strong> Get help directly from the team if you run into
              any issues.
            </li>
            <li>
              <strong>A Collaborative Community:</strong> Connect with other researchers who are
              testing the platform.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: 'Is the closed beta free? What happens after it ends?',
      answer: (
        <div>
          <p>
            Yes, the closed beta is completely free with unlimited access for the entire program.
            After the beta, we will transition to a subscription model to cover our operational
            costs. As a sincere thank you for helping us shape the product, all beta testers will
            receive a special offer when we launch.
          </p>
        </div>
      ),
    },
    {
      question: 'What are the limitations of the current beta?',
      answer: (
        <div>
          <p>
            Our beta is launching with a powerful, focused dataset to ensure the highest quality.
            The current database contains over <strong>800,000</strong> peer-reviewed papers from
            the field of <strong>cognitive neuroscience</strong>. Queries outside this domain will
            not yet return comprehensive results. We are actively expanding our database, and your
            feedback will help us prioritize which fields to focus on next.
          </p>
        </div>
      ),
    },
    {
      question: 'What is expected of me as a beta tester?',
      answer: (
        <div>
          <p>
            Your primary role is to use the platform for your real-world research tasks and let us
            know what you think. We ask our testers to be:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              <strong>Curious:</strong> Ask the tough questions you face in your research.
            </li>
            <li>
              <strong>Vocal:</strong> Share your feedback, ideas, and suggestions in our Discord.
            </li>
            <li>
              <strong>Honest:</strong> If something doesn&apos;t work or is confusing, we want to
              know about it.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: 'What types of questions can I ask?',
      answer: (
        <div>
          <p>
            <EtAiText /> excels at answering specific, evidence-based questions that require
            synthesizing information from across the literature. The more specific you are, the
            better.
          </p>
          <p className="mt-2">
            <strong>Good examples:</strong> &quot;What is the current consensus on the role of the
            hippocampus in spatial memory?&quot; or &quot;Compare the evidence for and against the
            theory of predictive coding in visual perception.&quot;
          </p>
          <p className="mt-2">
            <strong>Less effective examples:</strong> &quot;Tell me about the brain.&quot; (This is
            too broad).
          </p>
        </div>
      ),
    },
    {
      question: 'How do I provide feedback or report a bug?',
      answer: (
        <div>
          <p>
            Everything happens in our Discord server! This is the most effective way to get your
            thoughts directly to the development team. We have dedicated channels like{' '}
            <strong>#feedback-and-suggestions</strong> and <strong>#bug-reports</strong>. When
            reporting a bug, please include a brief description of the issue and the query you used.
          </p>
          <p>
            <a
              href="https://discord.gg/txuZbDUzdx"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-colors inline-block mt-4"
            >
              Join our Discord
            </a>
          </p>
        </div>
      ),
    },
    {
      question: 'Is my research data secure?',
      answer: (
        <div>
          <p>
            Absolutely. We treat the security and confidentiality of your research data as a top
            priority. Your queries and research are private. We do not share your data with third
            parties and we use industry-standard security practices to protect all user information.
            For more details, please see our <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </div>
      ),
    },
  ]

  return (
    <StaticPageLayout title="Help & Support">
      <SEO {...seoConfig} />
      <div className="space-y-8">
        <div className="bg-dark-secondary rounded-lg border border-dark-border p-8 text-center">
          <H2>
            Getting Started with <EtAiText />
          </H2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-dark-tertiary rounded-lg p-6 border border-dark-border">
              <H3>Ask Et AI</H3>
              <p className="text-gray-300 mb-4">
                Without signing up, you can chat with Et Ai about its capabilities! Note that
                without an account, Et Ai does not access our scientific database, so it cannot
                answer research questions.
              </p>
              <Link to="/" size="sm">
                Try the Chat Interface →
              </Link>
            </div>
            <div className="bg-dark-tertiary rounded-lg p-6 border border-dark-border">
              <H3>Join the Closed Beta</H3>
              <p className="text-gray-300 mb-4">
                Our beta is run through a private Discord community. This gives you a direct line to
                the founders to provide feedback and help us build the future of scientific
                research.
              </p>
              <Link to="https://discord.gg/txuZbDUzdx" size="sm">
                Join our Discord →
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-dark-secondary rounded-lg border border-dark-border p-8">
          <H2 className="mb-6">Frequently Asked Questions</H2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </div>
        </div>

        <div className="bg-dark-secondary rounded-lg border border-dark-border p-8 text-center">
          <H2>Have More Questions?</H2>
          <p className="text-gray-300 my-4 max-w-2xl mx-auto mb-2">
            The best way to get answers is to ask the team directly. Join our Discord community to
            chat with us and other researchers, or visit our <Link to="/contact">Contact</Link>{' '}
            page.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 text-center justify-center mt-4">
            <a
              href="https://discord.gg/txuZbDUzdx"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors text-center"
            >
              Ask in the Discord
            </a>
            <a
              href="/contact"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors text-center"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  )
}
